/**
 * PostHog analytics client for JumpBean.
 *
 * Provides type-safe event tracking with automatic user identification.
 */
import posthog from "posthog-js";
import { mergeConfig } from "./config";
let initialized = false;
let config = null;
/**
 * Initialize the analytics client.
 * Must be called before any tracking.
 */
export function initAnalytics(userConfig) {
    if (initialized) {
        console.warn("[Analytics] Already initialized");
        return;
    }
    config = mergeConfig(userConfig);
    if (config.disabled) {
        console.info("[Analytics] Disabled by configuration");
        initialized = true;
        return;
    }
    posthog.init(config.apiKey, {
        api_host: config.apiHost,
        capture_pageview: config.capturePageview,
        capture_pageleave: config.capturePageleave,
        persistence: "localStorage+cookie",
        // Respect Do Not Track
        respect_dnt: true,
        // Don't capture autocapture events - we use explicit events
        autocapture: false,
        // Disable session recording by default (can enable later)
        disable_session_recording: true,
        // Load feature flags on init
        bootstrap: {
            featureFlags: {},
        },
        loaded: (ph) => {
            if (config?.debug) {
                console.info("[Analytics] PostHog loaded", ph);
            }
        },
    });
    initialized = true;
    if (config.debug) {
        console.info("[Analytics] Initialized with config:", config);
    }
}
/**
 * Check if analytics is initialized and enabled
 */
function isEnabled() {
    if (!initialized) {
        console.warn("[Analytics] Not initialized. Call initAnalytics() first.");
        return false;
    }
    return !config?.disabled;
}
/**
 * Track an analytics event with type-safe properties.
 */
export function trackEvent(event, properties) {
    if (!isEnabled())
        return;
    posthog.capture(event, properties);
    if (config?.debug) {
        console.info("[Analytics] Event:", event, properties);
    }
}
/**
 * Identify a user (parent account).
 * Call this after login/signup.
 */
export function identifyUser(userId, properties) {
    if (!isEnabled())
        return;
    posthog.identify(userId, properties);
    if (config?.debug) {
        console.info("[Analytics] Identify user:", userId, properties);
    }
}
/**
 * Set the active child profile.
 * Uses PostHog groups to associate events with child profiles.
 */
export function setActiveChild(childId, properties) {
    if (!isEnabled())
        return;
    // Use groups to track child-specific events
    posthog.group("child", childId, properties);
    if (config?.debug) {
        console.info("[Analytics] Set active child:", childId, properties);
    }
}
/**
 * Clear the active child profile.
 */
export function clearActiveChild() {
    if (!isEnabled())
        return;
    posthog.resetGroups();
    if (config?.debug) {
        console.info("[Analytics] Cleared active child");
    }
}
/**
 * Update user properties without re-identifying.
 */
export function setUserProperties(properties) {
    if (!isEnabled())
        return;
    posthog.people.set(properties);
    if (config?.debug) {
        console.info("[Analytics] Set user properties:", properties);
    }
}
/**
 * Increment a numeric user property.
 * Uses capture with $set to update person properties.
 */
export function incrementUserProperty(property, value = 1) {
    if (!isEnabled())
        return;
    // Use capture with $set to update the property
    // PostHog will handle the increment on the server side through formulas/computed props
    posthog.capture("property_increment", {
        $set: { [property]: value },
        property_name: property,
        increment_value: value,
    });
}
/**
 * Reset the user (on logout).
 * Clears identification and creates a new anonymous ID.
 */
export function resetUser() {
    if (!isEnabled())
        return;
    posthog.reset();
    if (config?.debug) {
        console.info("[Analytics] Reset user");
    }
}
/**
 * Check if a feature flag is enabled.
 */
export function isFeatureEnabled(flagKey) {
    if (!isEnabled())
        return false;
    return posthog.isFeatureEnabled(flagKey) ?? false;
}
/**
 * Get feature flag value (for multivariate flags).
 */
export function getFeatureFlag(flagKey) {
    if (!isEnabled())
        return undefined;
    return posthog.getFeatureFlag(flagKey);
}
/**
 * Reload feature flags from the server.
 */
export function reloadFeatureFlags() {
    if (!isEnabled())
        return;
    posthog.reloadFeatureFlags();
}
/**
 * Register super properties that are sent with every event.
 */
export function registerSuperProperties(properties) {
    if (!isEnabled())
        return;
    posthog.register(properties);
    if (config?.debug) {
        console.info("[Analytics] Register super properties:", properties);
    }
}
/**
 * Opt the user out of tracking.
 */
export function optOut() {
    if (!initialized)
        return;
    posthog.opt_out_capturing();
    if (config?.debug) {
        console.info("[Analytics] User opted out");
    }
}
/**
 * Opt the user back into tracking.
 */
export function optIn() {
    if (!initialized)
        return;
    posthog.opt_in_capturing();
    if (config?.debug) {
        console.info("[Analytics] User opted in");
    }
}
/**
 * Check if the user has opted out.
 */
export function hasOptedOut() {
    if (!initialized)
        return false;
    return posthog.has_opted_out_capturing();
}
/**
 * Get the PostHog instance for advanced usage.
 * Prefer the typed helper functions above when possible.
 */
export function getPostHog() {
    return posthog;
}
//# sourceMappingURL=client.js.map